import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LifeBuoy, Send, CheckCircle2, Trash2 } from "lucide-react";

export default function AdminSupport() {
  const [tix, setTix] = useState([]);
  const [status, setStatus] = useState("open");
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [close, setClose] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/admin/support/tickets${status?"?status="+status:""}`); setTix(data.tickets||[]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const submit = async () => {
    if (!selected || !reply.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/support/reply", { ticket_id: selected.id, reply, close });
      toast.success("Reply saved");
      setReply(""); setSelected(null); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete this ticket from "${t.user_email}"?\n\nSubject: ${t.subject}\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/admin/support/tickets/${t.id}`);
      toast.success("Ticket deleted");
      if (selected?.id === t.id) { setSelected(null); setReply(""); }
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppLayout variant="admin" requireAdmin>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-nb-cyan">Admin · Support</div>
          <h1 className="font-heading font-bold text-3xl text-white mt-1"><LifeBuoy size={22} className="inline mr-2 -mt-1"/>Support Tickets</h1>
        </div>
        <select value={status} onChange={e=>setStatus(e.target.value)} className="nb-input py-1 text-sm" data-testid="ticket-filter">
          <option value="">All</option><option value="open">Open</option><option value="resolved">Resolved</option>
        </select>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="nb-card p-4 lg:col-span-1 max-h-[70vh] overflow-y-auto" data-testid="ticket-list">
          {tix.length===0 && <div className="text-slate-500 text-sm text-center py-8">No tickets.</div>}
          {tix.map(t=>(
            <div key={t.id} className={`relative rounded-lg border mb-2 ${selected?.id===t.id ? "border-nb-cyan bg-nb-cyan/5" : "border-white/5 bg-[#0A1120] hover:border-white/10"}`}>
              <button data-testid={`ticket-${t.id}`} onClick={()=>{ setSelected(t); setReply(t.admin_reply||""); }} className="w-full text-left p-3 pr-10">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-heading text-white text-sm truncate flex-1">{t.subject}</div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${t.status==="open"?"bg-amber-500/15 text-amber-300":"bg-[#8AE234]/15 text-[#8AE234]"}`}>{t.status}</span>
                </div>
                <div className="text-[10px] text-slate-500 font-mono mt-0.5 truncate">{t.user_email}</div>
                <div className="text-[11px] text-slate-400 mt-1 line-clamp-2 break-words">{t.message}</div>
                <div className="text-[10px] text-slate-600 mt-1 font-mono">{new Date(t.created_at).toLocaleString()}</div>
              </button>
              <button
                data-testid={`ticket-delete-${t.id}`}
                onClick={(e)=>{ e.stopPropagation(); remove(t); }}
                title="Delete ticket"
                className="absolute top-2 right-2 p-1.5 rounded-md text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 size={14}/>
              </button>
            </div>
          ))}
        </div>

        <div className="nb-card p-5 lg:col-span-2" data-testid="ticket-detail">
          {!selected && <div className="text-slate-500 text-sm text-center py-12">Select a ticket to view details and reply.</div>}
          {selected && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-nb-cyan">Ticket · {selected.status}</div>
              <div className="font-heading text-white text-xl mt-1">{selected.subject}</div>
              <div className="text-[11px] text-slate-500 font-mono mt-1">{selected.user_name} ({selected.user_email}) · {new Date(selected.created_at).toLocaleString()}</div>
              <div className="mt-4 rounded-lg border border-white/5 bg-[#0A1120] p-4 text-slate-200 text-sm whitespace-pre-wrap break-all min-w-0 overflow-hidden" style={{overflowWrap:"anywhere", wordBreak:"break-word"}}>{selected.message}</div>
              {selected.admin_reply && (
                <div className="mt-3 rounded-lg border border-[#8AE234]/20 bg-[#8AE234]/5 p-4 min-w-0 overflow-hidden">
                  <div className="text-[10px] uppercase tracking-widest text-[#8AE234]">Admin reply · {selected.admin_reply_by} · {selected.admin_replied_at && new Date(selected.admin_replied_at).toLocaleString()}</div>
                  <div className="text-slate-200 text-sm mt-1 whitespace-pre-wrap break-all" style={{overflowWrap:"anywhere", wordBreak:"break-word"}}>{selected.admin_reply}</div>
                </div>
              )}
              {selected.status !== "resolved" && (
                <div className="mt-4">
                  <label className="text-xs uppercase tracking-widest text-slate-400">Your reply</label>
                  <textarea data-testid="ticket-reply" value={reply} onChange={e=>setReply(e.target.value)} className="nb-input mt-1 h-32 text-sm" placeholder="Reply to this ticket…"/>
                  <label className="flex items-center gap-2 text-xs text-slate-300 mt-2">
                    <input type="checkbox" checked={close} onChange={e=>setClose(e.target.checked)} data-testid="ticket-close"/>
                    Mark as resolved after sending
                  </label>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button data-testid="ticket-submit" disabled={busy || !reply.trim()} onClick={submit} className="px-5 py-2 rounded-xl bg-nb-cyan text-black font-heading font-bold hover:opacity-90 disabled:opacity-40">
                      {close ? <><CheckCircle2 size={14} className="inline mr-1"/>Reply & Close</> : <><Send size={14} className="inline mr-1"/>Send Reply</>}
                    </button>
                    <button
                      data-testid="ticket-delete-detail"
                      onClick={()=>remove(selected)}
                      className="px-4 py-2 rounded-xl border border-red-500/30 text-red-300 hover:bg-red-500/10 text-sm font-heading"
                    >
                      <Trash2 size={14} className="inline mr-1 -mt-0.5"/>Delete without reply
                    </button>
                  </div>
                </div>
              )}
              {selected.status === "resolved" && (
                <div className="mt-4">
                  <button
                    data-testid="ticket-delete-resolved"
                    onClick={()=>remove(selected)}
                    className="px-4 py-2 rounded-xl border border-red-500/30 text-red-300 hover:bg-red-500/10 text-sm font-heading"
                  >
                    <Trash2 size={14} className="inline mr-1 -mt-0.5"/>Delete this ticket
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
